import { visionMessages } from "@studystraight/shared-config";

import type { VisionFailure } from "../hooks/useVisionTracking.js";

export interface VisionFailureBannerProps {
  failure: VisionFailure | null;
  onRetry: () => void;
  /** Hiện `detail` kỹ thuật. Chỉ bật khi người dùng đã mở Debug Mode (§25). */
  showDetail: boolean;
}

/**
 * Câu nói ra phải khớp với thứ đã hỏng.
 *
 * `init`/`timeout`/`stalled` là hỏng ở khâu nạp. `runtime` thì đã nạp xong rồi mới chết, và `webgl`
 * là thứ người dùng tự xử lý được — dùng chung một câu cho cả bốn thì banner chỉ nói được là "có
 * cái gì đó sai".
 */
function headline(stage: VisionFailure["stage"]): string {
  if (stage === "webgl") return visionMessages.webglBlocked;
  if (stage === "runtime") return visionMessages.crashed;
  return visionMessages.failed;
}

/**
 * Một chỗ duy nhất nói "vision không chạy được" — Setup và Monitoring dùng chung.
 *
 * Có riêng component vì hai màn hình từng cùng hiển thị hằng số "Đang tải bộ nhận diện tư thế…"
 * và cùng bỏ qua lỗi: tách ra thì lần sau thêm màn hình mới không lặp lại đúng lỗi đó.
 */
export function VisionFailureBanner({
  failure,
  onRetry,
  showDetail,
}: VisionFailureBannerProps): React.JSX.Element | null {
  if (!failure) return null;

  return (
    <div className="banner banner--error" role="alert">
      <span>{headline(failure.stage)}</span>
      {showDetail ? <code className="banner__detail">{`${failure.stage}: ${failure.detail}`}</code> : null}
      <button type="button" className="btn btn--secondary" onClick={onRetry}>
        {visionMessages.retry}
      </button>
    </div>
  );
}
